import { useEffect, useRef, useState } from "react"
import {
  DownloadOutlined,
  FileImageOutlined,
  LinkOutlined,
  UploadOutlined,
} from "@ant-design/icons"
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Segmented,
  Typography,
  Upload,
  message,
} from "antd"
import { Alert } from "~/components/Alert"
import type { UploadProps } from "antd"
import {
  maxVideoFileSize,
  validateLocalUploadFile,
} from "~/common/media/uploadedResources"
import {
  generateVideoCover,
  getVideoCoverErrorMessage,
} from "~/common/media/videoCover"
import { formatFileSize } from "~/common/utils"
import styles from "./index.css"

const { Text, Title } = Typography

type VideoSourceType = "local" | "url"

const defaultCaptureTime = 0
const coverFilename = "video-cover.jpg"

export const StaffVideoCover = () => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const operation = useRef<AbortController | null>(null)
  const [sourceType, setSourceType] = useState<VideoSourceType>("local")
  const [videoUrlInput, setVideoUrlInput] = useState("")
  const [videoUrl, setVideoUrl] = useState("")
  const [localVideoUrl, setLocalVideoUrl] = useState("")
  const [localVideoName, setLocalVideoName] = useState("")
  const [captureTime, setCaptureTime] = useState(defaultCaptureTime)
  const [videoDuration, setVideoDuration] = useState(0)
  const [coverUrl, setCoverUrl] = useState("")
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  const currentVideoUrl = sourceType === "local" ? localVideoUrl : videoUrl
  const hasVideoSource = currentVideoUrl.trim().length > 0

  useEffect(() => {
    return () => {
      if (localVideoUrl) {
        URL.revokeObjectURL(localVideoUrl)
      }
    }
  }, [localVideoUrl])

  useEffect(
    () => () => {
      if (coverUrl) URL.revokeObjectURL(coverUrl)
    },
    [coverUrl],
  )

  useEffect(() => {
    setIsGenerating(false)
    setCoverUrl("")
    setIsVideoReady(false)
    setVideoDuration(0)
    setCaptureTime(defaultCaptureTime)
    return () => {
      operation.current?.abort()
      operation.current = null
    }
  }, [currentVideoUrl])

  const loadUrlVideo = () => {
    const trimmedUrl = videoUrlInput.trim()

    if (!trimmedUrl) {
      void message.warning("请输入视频链接")
      return
    }

    setVideoUrl(trimmedUrl)
  }

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const validation = validateLocalUploadFile(file, "video")

    if (!validation.valid) {
      void message.warning(validation.message)
      return Upload.LIST_IGNORE
    }

    const nextUrl = URL.createObjectURL(file)

    if (localVideoUrl) {
      URL.revokeObjectURL(localVideoUrl)
    }

    setSourceType("local")
    setLocalVideoUrl(nextUrl)
    setLocalVideoName(file.name)

    return Upload.LIST_IGNORE
  }

  const handleLoadedMetadata = () => {
    const video = videoRef.current

    if (!video || !Number.isFinite(video.duration)) return

    setVideoDuration(video.duration)
    setCaptureTime(0)
    setIsVideoReady(true)
  }

  const handleVideoError = () => {
    setIsVideoReady(false)
    void message.error("视频加载失败，请检查视频文件或链接")
  }

  const generateCover = async () => {
    const video = videoRef.current

    if (!video || !hasVideoSource) {
      void message.warning("请先上传视频或输入视频链接")
      return
    }

    if (!isVideoReady) {
      void message.warning("视频仍在加载，请稍后再试")
      return
    }

    if (operation.current) return
    const controller = new AbortController()
    operation.current = controller
    setIsGenerating(true)

    try {
      const blob = await generateVideoCover(
        video,
        captureTime,
        controller.signal,
      )
      if (controller.signal.aborted) return
      setCoverUrl(URL.createObjectURL(blob))
      void message.success("封面已生成")
    } catch (error) {
      if (controller.signal.aborted) return
      const messageText = getVideoCoverErrorMessage(error)

      void message.error(messageText)
    } finally {
      if (operation.current === controller) {
        operation.current = null
        setIsGenerating(false)
      }
    }
  }

  const downloadCover = () => {
    if (!coverUrl) {
      void message.warning("请先生成封面")
      return
    }

    const link = document.createElement("a")

    link.href = coverUrl
    link.download = coverFilename
    link.click()
  }

  const clearVideo = () => {
    if (localVideoUrl) {
      URL.revokeObjectURL(localVideoUrl)
    }

    setVideoUrl("")
    setVideoUrlInput("")
    setLocalVideoUrl("")
    setLocalVideoName("")
    setCoverUrl("")
    setIsVideoReady(false)
    setVideoDuration(0)
    setCaptureTime(defaultCaptureTime)
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>视频生成封面</Title>
        <Text type="secondary">
          上传本地视频或输入视频链接，选择截取时间点后用 Canvas 生成封面图片。
        </Text>
      </Flex>

      <section className={styles.toolPanel}>
        <Flex
          vertical
          gap={18}
        >
          <Segmented<VideoSourceType>
            style={{ width: "fit-content" }}
            value={sourceType}
            options={[
              {
                icon: <UploadOutlined />,
                label: "本地上传",
                value: "local",
              },
              {
                icon: <LinkOutlined />,
                label: "视频链接",
                value: "url",
              },
            ]}
            onChange={setSourceType}
          />

          {sourceType === "local" ? (
            <Flex
              align="center"
              gap={12}
              wrap
            >
              <Upload
                accept="video/mp4"
                beforeUpload={beforeUpload}
                maxCount={1}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>选择本地视频</Button>
              </Upload>
              <Text type="secondary">
                {localVideoName ||
                  `仅支持 MP4，最大 ${formatFileSize(maxVideoFileSize)}`}
              </Text>
            </Flex>
          ) : (
            <Flex
              align="flex-start"
              gap={12}
            >
              <Form.Item className={styles.urlField}>
                <Input
                  allowClear
                  placeholder="请输入 mp4 视频链接"
                  value={videoUrlInput}
                  onChange={(event) => setVideoUrlInput(event.target.value)}
                  onPressEnter={loadUrlVideo}
                />
              </Form.Item>
              <Button
                type="primary"
                onClick={loadUrlVideo}
              >
                加载视频
              </Button>
            </Flex>
          )}

          <Alert
            showIcon
            level="weak"
            title="远程视频需要允许跨域访问，否则浏览器会阻止 Canvas 导出封面。"
          />
        </Flex>
      </section>

      <Flex
        className={styles.workspace}
        align="stretch"
        gap={18}
      >
        <section className={styles.previewPanel}>
          <Flex
            vertical
            gap={14}
          >
            <Flex
              align="center"
              justify="space-between"
            >
              <Title level={4}>视频预览</Title>
              <Button onClick={clearVideo}>清空</Button>
            </Flex>
            <div className={styles.videoBox}>
              {hasVideoSource ? (
                <video
                  ref={videoRef}
                  className={styles.video}
                  controls
                  crossOrigin="anonymous"
                  preload="metadata"
                  src={currentVideoUrl}
                  onError={handleVideoError}
                  onLoadedMetadata={handleLoadedMetadata}
                />
              ) : (
                <Flex
                  className={styles.placeholder}
                  align="center"
                  justify="center"
                  vertical
                  gap={10}
                >
                  <FileImageOutlined className={styles.placeholderIcon} />
                  <Text type="secondary">请先选择视频来源</Text>
                </Flex>
              )}
            </div>

            <Flex
              align="center"
              gap={12}
              wrap
            >
              <Text>截取时间</Text>
              <InputNumber
                min={0}
                max={videoDuration || undefined}
                precision={1}
                step={0.1}
                value={captureTime}
                onChange={(value) => setCaptureTime(value || 0)}
              />
              <Text type="secondary">
                {videoDuration
                  ? `视频时长 ${videoDuration.toFixed(1)}s`
                  : "等待视频加载"}
              </Text>
            </Flex>

            <Button
              type="primary"
              loading={isGenerating}
              disabled={!hasVideoSource}
              onClick={() => void generateCover()}
            >
              生成封面
            </Button>
          </Flex>
        </section>

        <section className={styles.coverPanel}>
          <Flex
            vertical
            gap={14}
          >
            <Flex
              align="center"
              justify="space-between"
            >
              <Title level={4}>封面图片</Title>
              <Button
                icon={<DownloadOutlined />}
                disabled={!coverUrl}
                onClick={downloadCover}
              >
                下载封面
              </Button>
            </Flex>
            <div className={styles.coverBox}>
              {coverUrl ? (
                <img
                  className={styles.coverImage}
                  alt="视频封面"
                  src={coverUrl}
                />
              ) : (
                <Flex
                  className={styles.placeholder}
                  align="center"
                  justify="center"
                  vertical
                  gap={10}
                >
                  <FileImageOutlined className={styles.placeholderIcon} />
                  <Text type="secondary">生成后展示封面图片</Text>
                </Flex>
              )}
            </div>
          </Flex>
        </section>
      </Flex>
    </Flex>
  )
}
